import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { WafService } from './waf.service';
import { catchError, of, switchMap, throwError } from 'rxjs';
import { WAF_CONFIG } from './waf.token';
import { isPlatformServer } from '@angular/common';

const setWafToken = (
  req: HttpRequest<unknown>,
  token: string,
  additonalHeaders: Record<string, string> = {}
) => {
  return token
    ? req.clone({
        setHeaders: {
          'x-aws-waf-token': token,
          ...additonalHeaders,
        },
      })
    : req;
};

/**
 * Checks if the HTTP event is a WAF challenge response.
 *
 * A WAF challenge response is identified by a 202 status code and the presence
 * of a specific header 'x-aws-waf-token' with the value 'challenge'.
 *
 * @param event - The HTTP event to check.
 * @returns True if the event is a WAF challenge response, false otherwise.
 *
 * @link https://docs.aws.amazon.com/waf/latest/APIReference/API_ChallengeAction.html
 */
const isWafChallengeResponse = (event: HttpEvent<unknown>) => {
  return (
    event instanceof HttpResponse &&
    event.status === 202 &&
    event.headers.has('x-aws-waf-token') &&
    event.headers.get('x-aws-waf-token') === 'challenge'
  );
};

/**
 * Handles challenge response after token submission.
 *
 * If the server returns a challenge response (status 202) despite having received
 * a WAF token, it indicates token rejection. In this case, we convert the response
 * to an error with an appropriate message to guide the user on next steps.
 */
const handleChallengeResponse = (event: HttpEvent<unknown>) => {
  if (isWafChallengeResponse(event)) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 500,
          statusText: 'Token rejected. Please call to place this order.',
        })
    );
  }
  return of(event);
};

/**
 * Handles errors related to CAPTCHA challenges.
 *
 * If the server responds with a 405 status code, it indicates that a CAPTCHA
 * challenge is required. This function triggers the `renderCaptcha` method
 * from the `WafService`, which is responsible for displaying the CAPTCHA to
 * the user and obtaining a valid token upon successful completion.
 *
 * Once the token is obtained, the original HTTP request is retried with the
 * new token added to its headers using the `setWafToken` function.
 *
 * If the error is not a 405 status code, the function simply propagates the
 * error by returning a `throwError` observable.
 *
 * @param error - The HTTP error response received from the server.
 * @param req - The original HTTP request that triggered the error.
 * @param wafService - The service responsible for handling WAF-related operations.
 * @param next - The next handler in the interceptor chain, used to retry the request.
 * @returns An observable that either retries the request with a new token or propagates the error.
 */
const handleCaptchaError = (
  error: HttpErrorResponse,
  req: HttpRequest<unknown>,
  wafService: WafService,
  next: HttpHandlerFn
) => {
  if (error.status === 405 && !req.headers.has('X-Skip-Captcha')) {
    return wafService
      .renderCaptcha()
      .pipe(
        switchMap((token) =>
          next(setWafToken(req, token, { 'X-Skip-Captcha': 'true' }))
        )
      );
  }
  return throwError(() => error);
};

export const wafInterceptor: HttpInterceptorFn = (req, next) => {
  const wafService = inject(WafService);
  const wafConfig = inject(WAF_CONFIG);
  const platformId = inject(PLATFORM_ID);
  if (!req.url.startsWith(wafConfig.apiUrl) || isPlatformServer(platformId)) {
    return next(req);
  }
  return wafService.getToken().pipe(
    switchMap((token) => next(setWafToken(req, token))),
    switchMap((event) => handleChallengeResponse(event)),
    catchError((error) => handleCaptchaError(error, req, wafService, next))
  );
};
